import { useCallback, useRef } from 'react';
import { ColDefConstraint, mergeColumnState } from './mergeColumnState';

type ColumnStateLike = Record<string, unknown>;

type UseAgGridColumnStatePersistenceOptions = {
  storageKey: string;
  defaultState: ColumnStateLike[];
  /** When false, skips localStorage read/write (caller still owns apply if needed). */
  enabled?: boolean;
  /**
   * When provided, saved state is reconciled with these column definitions
   * before being applied. This enforces pinning constraints and inserts any
   * newly added columns at a sensible position, while preserving the user's
   * existing layout for all other columns.
   *
   * Has no effect when there is no saved state (falls through to `defaultState`).
   */
  columnDefs?: ColDefConstraint[];
};

/**
 * Optional helper: apply saved column state on grid ready, persist on column layout events.
 */
export function useAgGridColumnStatePersistence({
  storageKey,
  defaultState,
  enabled = true,
  columnDefs,
}: UseAgGridColumnStatePersistenceOptions) {
  const columnStateLoadedRef = useRef(false);

  const applyOnGridReady = useCallback(
    (api: { applyColumnState: (args: { state: ColumnStateLike[]; applyOrder: boolean }) => void }) => {
      if (!enabled) {
        columnStateLoadedRef.current = true;
        return;
      }
      const savedRaw = localStorage.getItem(storageKey);
      if (savedRaw) {
        try {
          const parsed = JSON.parse(savedRaw) as ColumnStateLike[];
          const stateToApply =
            columnDefs && columnDefs.length > 0 ? mergeColumnState(parsed, columnDefs) : parsed;

          api.applyColumnState({ state: stateToApply, applyOrder: true });
        } catch {
          api.applyColumnState({ state: defaultState, applyOrder: true });
        }
      } else {
        api.applyColumnState({ state: defaultState, applyOrder: true });
      }
      columnStateLoadedRef.current = true;
    },
    [columnDefs, defaultState, enabled, storageKey]
  );

  const onColumnEvent = useCallback(
    (params: { api?: { getColumnState: () => ColumnStateLike[] }; type?: string; finished?: boolean }) => {
      if (!enabled || !columnStateLoadedRef.current || !params?.api) {
        return;
      }
      const t = params.type;
      if (
        t === 'columnMoved' ||
        t === 'columnVisible' ||
        t === 'columnPinned' ||
        t === 'columnResized' ||
        params.finished
      ) {
        const state = params.api.getColumnState();
        localStorage.setItem(storageKey, JSON.stringify(state));
      }
    },
    [enabled, storageKey]
  );

  return { applyOnGridReady, onColumnEvent, columnStateLoadedRef };
}
